import { useEffect, useState } from "react"
import { useAuth } from "@/lib/auth"
import { Shell } from "@/components/Shell"
import {
  listLabels,
  listRecipes,
  type Label,
  type RecipeSummary,
} from "@/lib/api"
import { LabelChip } from "@/components/LabelChip"

/**
 * Recipes — the saved-recipe library.
 *
 * Aesthetic: editorial table-of-contents. Each recipe is a row with a big
 * display-font name, a thin meta line, and a hairline rule below. No card
 * chrome — feels like turning the pages of a recipe journal.
 *
 * Origin filter:
 *  - 'saved' (default) → things explicitly saved as recipes; the library.
 *  - 'log'             → auto-saved composed meals (Log once); hidden from
 *                        the library by default but recoverable here.
 *  - 'all'             → everything for forensics.
 *
 * Tap a row → /recipes/{id} (detail view; not built yet — for now we just
 * preview the macros inline). Add a "+ New recipe" CTA that links to the
 * existing parse-and-save flow at /recipes/new.
 */
type Origin = "saved" | "log" | "all"

export function RecipesPage() {
  const auth = useAuth()
  const [origin, setOrigin] = useState<Origin>("saved")
  const [labelFilter, setLabelFilter] = useState<string | null>(null)
  const [recipes, setRecipes] = useState<RecipeSummary[] | null>(null)
  const [labels, setLabels] = useState<Label[]>([])
  const [err, setErr] = useState<string | null>(null)

  // Recipes — refetched on origin OR label-filter change.
  useEffect(() => {
    setRecipes(null)
    setErr(null)
    listRecipes(origin, labelFilter ?? undefined)
      .then(setRecipes)
      .catch((e: any) =>
        setErr(e?.response?.data?.error || e?.message || "Couldn't load recipes."),
      )
  }, [origin, labelFilter])

  // Label vocabulary — fetch once; the filter strip uses this to render chips.
  useEffect(() => {
    listLabels()
      .then(setLabels)
      .catch(() => {
        // Silent — labels are nice-to-have on this page; the recipes list
        // still works without them.
      })
  }, [])

  if (auth.status !== "authed") return null

  return (
    <Shell
      currentPath="/recipes"
      navItems={[
        { href: "/", label: "Dashboard" },
        { href: "/today", label: "Today" },
        { href: "/performance", label: "Performance" },
        { href: "/ingredients", label: "Ingredients" },
        { href: "/recipes", label: "Recipes" },
        { href: "/weight", label: "Weight" },
        { href: "/profile", label: "Profile" },
      ]}
    >
      <div className="max-w-5xl mx-auto px-6 sm:px-10 py-12 sm:py-16">
        <p className="eyebrow mb-3 animate-ink-in">recipes · library</p>
        <h1
          className="font-display text-display tracking-tightest mb-3 animate-fade-up"
          style={{ animationDelay: "120ms" }}
        >
          Your <span className="italic text-moss">cookbook</span>.
        </h1>
        <p
          className="font-sans text-base text-muted-foreground max-w-xl leading-relaxed mb-10 animate-fade-up"
          style={{ animationDelay: "240ms" }}
        >
          Saved recipes from{" "}
          <a href="/recipes/new" className="underline decoration-moss/40 underline-offset-2 hover:text-foreground">
            parse-and-save
          </a>{" "}
          and explicit "Save &amp; log" entries from the meal composer. Tap any
          row to view its breakdown.
        </p>

        {/* Filter row + new-recipe CTA */}
        <div className="flex items-baseline justify-between gap-4 mb-4">
          <OriginFilter value={origin} onChange={setOrigin} />
          <a
            href="/recipes/new"
            className="font-sans text-sm tracking-tight bg-foreground text-background px-5 py-2.5 rounded hover:bg-moss transition-colors whitespace-nowrap"
          >
            + new recipe
          </a>
        </div>

        {/* Label filter strip — only renders when the user has any labels. */}
        {labels.length > 0 && (
          <div className="mb-6">
            <div className="flex items-baseline justify-between mb-2">
              <p className="eyebrow">labels</p>
              {labelFilter && (
                <button
                  type="button"
                  onClick={() => setLabelFilter(null)}
                  className="eyebrow text-muted-foreground hover:text-persimmon transition-colors"
                >
                  clear filter
                </button>
              )}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {labels.map((l) => (
                <LabelChip
                  key={l.id}
                  label={l}
                  selected={labelFilter === l.id}
                  onClick={() =>
                    setLabelFilter((cur) => (cur === l.id ? null : l.id))
                  }
                />
              ))}
            </div>
          </div>
        )}

        {err && (
          <div className="px-4 py-3 border-l-2 border-persimmon bg-persimmon/[0.04] mb-6">
            <p className="font-sans text-sm text-persimmon">{err}</p>
          </div>
        )}

        {recipes === null && !err ? (
          <p className="font-mono text-xs text-muted-foreground">loading…</p>
        ) : recipes && recipes.length === 0 ? (
          <EmptyState origin={origin} />
        ) : (
          <RecipeList recipes={recipes ?? []} showOriginTag={origin === "all"} />
        )}
      </div>
    </Shell>
  )
}

function OriginFilter({
  value,
  onChange,
}: {
  value: Origin
  onChange: (o: Origin) => void
}) {
  return (
    <div
      role="tablist"
      aria-label="Recipe origin filter"
      className="inline-flex border border-border rounded overflow-hidden"
    >
      {(
        [
          { id: "saved", label: "saved" },
          { id: "log", label: "from log" },
          { id: "all", label: "all" },
        ] as const
      ).map((o) => {
        const active = value === o.id
        return (
          <button
            key={o.id}
            type="button"
            role="tab"
            aria-selected={active}
            onClick={() => onChange(o.id)}
            className={`px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider transition-colors ${
              active
                ? "bg-foreground text-background"
                : "bg-surface text-muted-foreground hover:text-foreground"
            }`}
          >
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

function RecipeList({
  recipes,
  showOriginTag,
}: {
  recipes: RecipeSummary[]
  showOriginTag: boolean
}) {
  return (
    <ul className="border-t border-border">
      {recipes.map((r, i) => (
        <li
          key={r.id}
          className="border-b border-border animate-fade-up"
          style={{ animationDelay: `${Math.min(i * 25, 600)}ms` }}
        >
          <a
            href={`/recipes/${r.id}`}
            className="block py-5 sm:py-6 group hover:bg-muted/30 transition-colors -mx-2 px-2"
          >
            <div className="flex items-baseline justify-between gap-4">
              <div className="min-w-0">
                <div className="flex items-baseline gap-2 mb-1.5 flex-wrap">
                  {r.origin === "log" && (
                    <span
                      className="font-mono text-[10px] uppercase tracking-wider text-moss border border-moss/40 rounded px-1.5 py-0.5"
                      title="Auto-saved by the meal composer"
                    >
                      log
                    </span>
                  )}
                  {showOriginTag && r.origin === "saved" && (
                    <span
                      className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground border border-border rounded px-1.5 py-0.5"
                    >
                      saved
                    </span>
                  )}
                  <h2 className="font-display text-2xl sm:text-3xl tracking-tightest leading-tight group-hover:text-moss transition-colors">
                    {r.name}
                  </h2>
                </div>
                <p className="font-mono text-xs text-muted-foreground tabular-nums">
                  {r.calories_per_100g.toFixed(0)} kcal/100g ·{" "}
                  {r.protein_per_100g.toFixed(1)}g protein/100g · yields{" "}
                  {r.total_yield_g}g · {fmtDate(r.created_at)}
                </p>
                {r.labels.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {r.labels.map((l) => (
                      <LabelChip key={l.id} label={l} />
                    ))}
                  </div>
                )}
              </div>
              <span
                aria-hidden
                className="font-mono text-sm text-muted-foreground group-hover:text-moss transition-colors whitespace-nowrap"
              >
                →
              </span>
            </div>
          </a>
        </li>
      ))}
    </ul>
  )
}

function EmptyState({ origin }: { origin: Origin }) {
  const copy =
    origin === "saved"
      ? {
          eyebrow: "your library is empty",
          title: "Nothing saved yet.",
          body: (
            <>
              Parse a recipe at{" "}
              <a
                href="/recipes/new"
                className="underline decoration-moss/40 underline-offset-2 hover:text-foreground"
              >
                /recipes/new
              </a>
              , or use <span className="italic">Save &amp; log</span> when
              composing a meal on /today.
            </>
          ),
        }
      : origin === "log"
        ? {
            eyebrow: "nothing logged yet",
            title: "No composed meals on record.",
            body: (
              <>
                Compose a meal from <a href="/today" className="underline decoration-moss/40 underline-offset-2 hover:text-foreground">today</a>{" "}
                and tap <span className="italic">Log once</span>. Those entries
                live here so you can re-pick them later by name.
              </>
            ),
          }
        : {
            eyebrow: "no recipes",
            title: "Empty cookbook.",
            body: (
              <>
                Nothing parsed yet, in any form. Start with{" "}
                <a
                  href="/recipes/new"
                  className="underline decoration-moss/40 underline-offset-2 hover:text-foreground"
                >
                  /recipes/new
                </a>
                .
              </>
            ),
          }

  return (
    <div className="py-12">
      <p className="eyebrow text-muted-foreground mb-3">{copy.eyebrow}</p>
      <h2 className="font-display text-3xl tracking-tightest mb-4">
        {copy.title}
      </h2>
      <p className="font-sans text-base text-muted-foreground max-w-md leading-relaxed">
        {copy.body}
      </p>
    </div>
  )
}

function fmtDate(iso: string): string {
  try {
    const d = new Date(iso)
    return d.toLocaleDateString(undefined, {
      month: "short",
      day: "numeric",
      year:
        d.getFullYear() === new Date().getFullYear() ? undefined : "numeric",
    })
  } catch {
    return iso
  }
}
